import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import type { Submission, PaginatedResponse } from '../types'
import { getAdminToken, clearAdminToken } from './authToken'

type ApiErrorBody = { error?: string; message?: string }



function getLoggedInName(): string | null {
  try {
    const token = getAdminToken()
    if (!token) return null
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.name || payload.username || null
  } catch {
    return null
  }
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  submitted: { label: 'SUBMITTED', bg: 'bg-green-100', text: 'text-green-800' },
  in_progress: { label: 'IN PROGRESS', bg: 'bg-yellow-100', text: 'text-yellow-800' },
  abandoned: { label: 'ABANDONED', bg: 'bg-red-100', text: 'text-red-800' },
}

function formatDateTime(dateString: string) {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

export default function AdminList() {
  const [rows, setRows] = useState<Submission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const loggedInName = getLoggedInName()

  // Read filter state from URL params
  const page = parseInt(searchParams.get('page') || '1')
  const limit = parseInt(searchParams.get('limit') || '25')
  const search = searchParams.get('search') || ''
  const statusFilter = searchParams.get('status') || ''
  const sort = searchParams.get('sort') || 'created_at'
  const order = searchParams.get('order') || 'desc'

  // Local search input (debounced)
  const [searchInput, setSearchInput] = useState(search)

  const updateParams = useCallback((updates: Record<string, string>) => {
    const next = new URLSearchParams(searchParams)
    for (const [k, v] of Object.entries(updates)) {
      if (v) next.set(k, v)
      else next.delete(k)
    }
    // Reset to page 1 when filters change (unless page itself is being set)
    if (!('page' in updates)) next.set('page', '1')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const handleLogout = () => {
    clearAdminToken()
    navigate('/admin/login')
  }

  const handleDelete = async (id: string, studyId: string) => {
    try {
      const token = getAdminToken()
      const response = await fetch(`/api/admin/submissions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const result = await response.json().catch(() => ({} as ApiErrorBody)) as ApiErrorBody
      if (response.ok) {
        setDeleteConfirm(null)
        // Refresh current page
        fetchData()
      } else {
        alert(`Error: ${result.error || result.message || 'Delete failed'}`)
      }
    } catch (err) {
      alert(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  const handleExport = async (format: 'csv' | 'json') => {
    setExporting(true)
    try {
      const token = getAdminToken()
      const params = new URLSearchParams()
      params.set('format', format)
      if (search) params.set('search', search)
      if (statusFilter) params.set('status', statusFilter)

      const response = await fetch(`/api/admin/export?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({} as ApiErrorBody)) as ApiErrorBody
        alert(`Export failed: ${err.error || err.message || 'Unknown export error'}`)
        return
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `jointai-export-${new Date().toISOString().slice(0, 10)}.${format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      alert(`Export error: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setExporting(false)
    }
  }

  const handleSort = (column: string) => {
    if (sort === column) {
      updateParams({ sort: column, order: order === 'asc' ? 'desc' : 'asc' })
    } else {
      updateParams({ sort: column, order: 'desc' })
    }
  }

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const token = getAdminToken()
      const params = new URLSearchParams()
      params.set('page', String(page))
      params.set('limit', String(limit))
      if (search) params.set('search', search)
      if (statusFilter) params.set('status', statusFilter)
      params.set('sort', sort)
      params.set('order', order)

      const r = await fetch(`/api/admin/submissions?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!r.ok) throw new Error(`Failed to fetch: ${r.status}`)
      const j: PaginatedResponse<Submission> = await r.json()
      setRows(j.items || [])
      setTotal(j.total || 0)
      setTotalPages(j.totalPages || 0)
    } catch (e) {
      Sentry.captureException(e, { tags: { action: 'load_submissions' } })
      setError(e instanceof Error ? e.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [page, limit, search, statusFilter, sort, order])

  useEffect(() => { fetchData() }, [fetchData])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        updateParams({ search: searchInput })
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const SortIcon = ({ column }: { column: string }) => {
    if (sort !== column) return <span className="text-gray-300 ml-1">{'\u2195'}</span>
    return <span className="ml-1">{order === 'asc' ? '\u25B2' : '\u25BC'}</span>
  }

  return (
    <div className='min-h-screen p-6 bg-gradient-to-br from-slate-50 to-slate-100'>
      <div className='max-w-6xl mx-auto space-y-4'>
        {/* Header */}
        <div className='bg-white rounded-xl border-b border-gray-200 px-5 py-3'>
          <div className='flex items-center justify-between'>
            <img src='/images/logo.png' alt='JointAi' className='h-7' />
            <div className="flex items-center space-x-4">
              {loggedInName && (
                <span className="text-sm text-slate-500">Hi {loggedInName}</span>
              )}
              <button
                onClick={handleLogout}
                className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {error ? (
          <div className='bg-white rounded-xl shadow p-6'>
            <h2 className='text-xl font-bold text-red-600'>Error Loading Submissions</h2>
            <p className='text-slate-600 mt-2'>{error}</p>
            <button onClick={() => { setError(null); fetchData() }} className='mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'>
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Search, Filters, Export Bar */}
            <div className='bg-white rounded-xl shadow p-4'>
              <div className='flex flex-wrap items-center gap-3'>
                {/* Search */}
                <div className='flex-1 min-w-[200px]'>
                  <input
                    type='text'
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder='Search by Study ID...'
                    className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => updateParams({ status: e.target.value })}
                  className='px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'
                >
                  <option value=''>All Statuses</option>
                  <option value='submitted'>Submitted</option>
                  <option value='in_progress'>In Progress</option>
                  <option value='abandoned'>Abandoned</option>
                </select>

                {/* Export Buttons */}
                <div className='flex gap-2'>
                  <button
                    onClick={() => handleExport('csv')}
                    disabled={exporting}
                    className='px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50'
                  >
                    {exporting ? 'Exporting...' : 'Export CSV'}
                  </button>
                  <button
                    onClick={() => handleExport('json')}
                    disabled={exporting}
                    className='px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50'
                  >
                    JSON
                  </button>
                </div>

                {/* Refresh */}
                <button onClick={fetchData} className='px-3 py-2 text-gray-500 hover:text-gray-700 text-sm'>
                  Refresh
                </button>
              </div>

              {/* Active filters summary */}
              {(search || statusFilter) && (
                <div className='mt-3 flex items-center gap-2 text-sm text-gray-600'>
                  <span>Showing {total} result{total !== 1 ? 's' : ''}</span>
                  {search && (
                    <span className='bg-blue-50 text-blue-700 px-2 py-0.5 rounded'>
                      search: "{search}"
                      <button onClick={() => { setSearchInput(''); updateParams({ search: '' }) }} className='ml-1 text-blue-400 hover:text-blue-600'>&times;</button>
                    </span>
                  )}
                  {statusFilter && (
                    <span className='bg-blue-50 text-blue-700 px-2 py-0.5 rounded'>
                      status: {statusFilter}
                      <button onClick={() => updateParams({ status: '' })} className='ml-1 text-blue-400 hover:text-blue-600'>&times;</button>
                    </span>
                  )}
                  <button
                    onClick={() => { setSearchInput(''); updateParams({ search: '', status: '' }) }}
                    className='text-blue-600 hover:underline'
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Table */}
            <div className='bg-white rounded-xl shadow'>
              {loading ? (
                <div className='text-center py-12'>
                  <p className='text-gray-500'>Loading submissions...</p>
                </div>
              ) : rows.length === 0 ? (
                <div className='text-center py-12'>
                  <p className='text-gray-400'>{search || statusFilter ? 'No matching submissions' : 'No submissions yet'}</p>
                </div>
              ) : (
                <>
                  <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                      <thead className='text-gray-600 bg-gray-50 border-b'>
                        <tr>
                          <th className='text-left p-3 cursor-pointer hover:bg-gray-100' onClick={() => handleSort('study_id')}>
                            Study ID <SortIcon column='study_id' />
                          </th>
                          <th className='text-left p-3 cursor-pointer hover:bg-gray-100' onClick={() => handleSort('status')}>
                            Status <SortIcon column='status' />
                          </th>
                          <th className='text-left p-3 cursor-pointer hover:bg-gray-100' onClick={() => handleSort('step')}>
                            Progress <SortIcon column='step' />
                          </th>
                          <th className='text-left p-3 cursor-pointer hover:bg-gray-100 min-w-[130px]' onClick={() => handleSort('created_at')}>
                            Started <SortIcon column='created_at' />
                          </th>
                          <th className='text-left p-3 cursor-pointer hover:bg-gray-100 min-w-[130px]' onClick={() => handleSort('updated_at')}>
                            Updated <SortIcon column='updated_at' />
                          </th>
                          <th className='text-left p-3'>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(r => {
                          const studyId = r.study_id || 'Not Set'
                          const startedTime = formatDateTime(r.created_at)
                          const updatedTime = formatDateTime(r.updated_at)
                          const statusCfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.abandoned

                          return (
                            <tr key={r.id} className='border-t border-gray-100 hover:bg-gray-50 cursor-pointer' onClick={() => navigate(`/admin/${r.id}`)}>
                              <td className='p-3'>
                                <div className='font-semibold text-gray-900'>{studyId}</div>
                              </td>
                              <td className='p-3'>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.text}`}>
                                  {statusCfg.label}
                                </span>
                              </td>
                              <td className='p-3'>
                                <div className='flex items-center gap-2'>
                                  <div className='w-16 bg-gray-200 rounded-full h-2'>
                                    <div
                                      className={`h-2 rounded-full ${r.status === 'submitted' ? 'bg-green-500' : 'bg-blue-500'}`}
                                      style={{width: `${(r.step / 24) * 100}%`}}
                                    />
                                  </div>
                                  <span className='text-xs text-gray-600'>{r.step}/24</span>
                                </div>
                              </td>
                              <td className='p-3 text-xs text-gray-700'>{startedTime}</td>
                              <td className='p-3 text-xs text-gray-700'>{updatedTime}</td>
                              <td className='p-3' onClick={(e) => e.stopPropagation()}>
                                <div className='flex items-center gap-2'>
                                  <Link
                                    to={`/admin/${r.id}`}
                                    className='bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 transition-colors'
                                  >
                                    Review
                                  </Link>
                                  {deleteConfirm === r.id ? (
                                    <div className='flex items-center gap-1'>
                                      <button
                                        onClick={() => handleDelete(r.id, studyId)}
                                        className='bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700'
                                      >
                                        Confirm
                                      </button>
                                      <button
                                        onClick={() => setDeleteConfirm(null)}
                                        className='bg-gray-400 text-white px-2 py-1 rounded text-xs hover:bg-gray-500'
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setDeleteConfirm(r.id)}
                                      className='text-red-400 hover:text-red-600 text-xs px-2 py-1'
                                      title='Delete submission'
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className='flex items-center justify-between px-4 py-3 border-t border-gray-100'>
                    <div className='text-sm text-gray-600'>
                      Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} of {total}
                    </div>
                    <div className='flex items-center gap-2'>
                      <select
                        value={limit}
                        onChange={(e) => updateParams({ limit: e.target.value, page: '1' })}
                        className='px-2 py-1 border border-gray-300 rounded text-sm bg-white'
                      >
                        <option value='10'>10</option>
                        <option value='25'>25</option>
                        <option value='50'>50</option>
                        <option value='100'>100</option>
                      </select>
                      <span className='text-sm text-gray-500'>per page</span>

                      <div className='flex items-center gap-1 ml-4'>
                        <button
                          onClick={() => updateParams({ page: '1' })}
                          disabled={page <= 1}
                          className='px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-30 hover:bg-gray-50'
                        >
                          First
                        </button>
                        <button
                          onClick={() => updateParams({ page: String(page - 1) })}
                          disabled={page <= 1}
                          className='px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-30 hover:bg-gray-50'
                        >
                          Prev
                        </button>
                        <span className='px-3 py-1 text-sm text-gray-600'>
                          Page {page} of {totalPages}
                        </span>
                        <button
                          onClick={() => updateParams({ page: String(page + 1) })}
                          disabled={page >= totalPages}
                          className='px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-30 hover:bg-gray-50'
                        >
                          Next
                        </button>
                        <button
                          onClick={() => updateParams({ page: String(totalPages) })}
                          disabled={page >= totalPages}
                          className='px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-30 hover:bg-gray-50'
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Delete confirmation overlay for individual submission */}
      {deleteConfirm && (
        <div className='sr-only' aria-live='polite'>
          Confirm deletion for submission
        </div>
      )}
    </div>
  )
}
